<script setup lang="ts">
import { transformEditUrl } from '@modules/contrib/betr/widgets/customWidgetEmployeeSummary/utils'
import { computed } from 'vue'

import { EMPLOYEE_INSURANCE_WIDGET_NAME } from './constants'

import type { CustomWidget } from '@/api/widgets'

import { transformPath } from '@/utils/transformer'

const props = defineProps<{
  customWidget: CustomWidget
}>()

const isError = computed(() => props.customWidget.Data.Style === 'error')
const isInfo = computed(() => props.customWidget.Data.Style === 'info')
const hasStatus = computed(() => isError.value || isInfo.value)

const actionRow = computed(() => {
  return props.customWidget.Data.Raw.find(row => row.RowType === 'button')
})

const contentRows = computed(() => {
  return props.customWidget.Data.Raw.filter(row => row.RowType !== 'button')
})

const getActionUrl = (url: string) => {
  return props.customWidget.Info.MachineName === EMPLOYEE_INSURANCE_WIDGET_NAME
    ? transformEditUrl(url)
    : transformPath(url)
}
</script>

<template>
  <VCard
    v-if="customWidget.Data.Show"
    :class="[
      'employee-summary-card',
      {
        'employee-summary-card--error': isError,
        'employee-summary-card--info': isInfo,
      }
    ]"
    rounded="lg"
    elevation="0"
  >
    <VCardTitle class="employee-summary-card__title">
      <span class="employee-summary-card__heading">
        {{ customWidget.Info.Name }}
      </span>

      <VBtn
        v-if="actionRow"
        :to="getActionUrl(actionRow.Url)"
        :aria-label="actionRow.Title"
        :title="actionRow.Title"
        icon="mdi-plus"
        :color="hasStatus ? undefined : 'primary'"
        :variant="hasStatus ? 'outlined' : 'flat'"
        size="small"
        class="employee-summary-card__action"
      />
    </VCardTitle>

    <VCardText
      v-if="contentRows.length"
      class="employee-summary-card__content"
    >
      <div
        v-for="(row, index) in contentRows"
        :key="`${row.RowType}-${row.Title}-${index}`"
        class="employee-summary-card__row"
      >
        <template v-if="row.RowType === 'text'">
          <a
            v-if="row.Url"
            :href="transformPath(row.Url)"
            :class="[
              'employee-summary-card__link',
              {
                'employee-summary-card__link--more': row.Title.startsWith('Посмотреть все')
              }
            ]"
          >
            {{ row.Title }}
          </a>
          <span v-else>{{ row.Title }}</span>
        </template>
      </div>
    </VCardText>
  </VCard>
</template>

<style scoped lang="scss">
.employee-summary-card {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);

  &__title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem 1.25rem 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    white-space: normal;
  }

  &__heading {
    min-width: 0;
  }

  &__action {
    flex: 0 0 auto;
    transition: background-color 0.2s ease;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.5rem 1.25rem 1.25rem;
    font-size: 0.8125rem;
    line-height: 1.5;
  }

  &__row:empty {
    display: none;
  }

  &__link {
    color: rgb(var(--v-theme-primary));
    text-decoration: none;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
      text-underline-offset: 0.15em;
    }

    &--more {
      text-decoration: underline;
      text-underline-offset: 0.15em;
    }
  }

  &--error,
  &--info {
    .employee-summary-card__action {
      color: inherit;
      background: transparent;

      &:hover,
      &:focus-visible {
        background: color-mix(in srgb, currentColor 8%, transparent);
      }
    }
  }

  &--error {
    color: var(--ui-danger);
    background: var(--ui-danger-surface);
    border-color: color-mix(in srgb, var(--ui-danger) 28%, transparent);
  }

  &--info {
    color: var(--ui-success);
    background: var(--ui-success-surface);
    border-color: color-mix(in srgb, var(--ui-success) 28%, transparent);
  }
}
</style>
